import { format } from "date-fns";
import { useMemo } from "react";
import { IconButton, Modal, Typography } from "@/components/atoms";
import { DataTable, DataTableColumnDef } from "@/components/molecules";
import { useProfile } from "@/hooks";
import { BookingPaymentLog } from "@/redux/slices/booking/types";
import { PaymentHistoryStatus } from "../PaymentHistoryStatus";
import { EmptyPaymentAttempts } from "../emptyStates/EmptyPaymentAttempts";
import { getPaymentAttemptsFromToText } from "../helpers";

interface PaymentAttemptsModalProps {
  open: boolean;
  onClose: () => void;
  selectedPaymentLog: BookingPaymentLog;
}

interface PaymentAttemptTableData {
  id: string;
  dateTime: JSX.Element;
  status: JSX.Element;
  fromTo: string;
  description: string;
}

const paymentAttemptsColumns: DataTableColumnDef<PaymentAttemptTableData>[] = [
  {
    header: "Date & Time",
    cell: (props) => props.row.original.dateTime,
    size: 120,
    enableSorting: false,
  },
  {
    header: "Status",
    cell: (props) => props.row.original.status,
    size: 80,
    enableSorting: false,
  },
  {
    header: "From → To",
    accessorKey: "fromTo",
    size: 180,
    enableSorting: false,
  },
  {
    header: "Description",
    accessorKey: "description",
    enableSorting: false,
  },
];

export const PaymentAttemptsModal = ({ open, onClose, selectedPaymentLog }: PaymentAttemptsModalProps) => {
  const { dateFormat, fullTimeFormat } = useProfile();

  const tableData: PaymentAttemptTableData[] = useMemo(() => {
    return [...selectedPaymentLog.history]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .map((historyItem) => ({
        id: historyItem.id,
        dateTime: (
          <div className="grid">
            <span className="leading-snug">{format(new Date(historyItem.createdAt), dateFormat)}</span>
            <span className="leading-snug text-neutral-dark-gray">{format(new Date(historyItem.createdAt), fullTimeFormat)}</span>
          </div>
        ),
        status: <PaymentHistoryStatus status={historyItem.status} />,
        fromTo: getPaymentAttemptsFromToText(selectedPaymentLog.type, historyItem.category, historyItem.paidTo),
        description: `${historyItem.description}.${historyItem.reason ? ` Reason: ${historyItem.reason}` : ""}` || "Payment processed",
      }));
  }, [selectedPaymentLog, dateFormat, fullTimeFormat]);

  const renderHeader = () => (
    <div className="flex items-center justify-between">
      <Typography variant="title">Payment Attempts</Typography>
      <IconButton className="border-none p-2" iconName="close" isCustomIcon iconSize="md" variant="secondary" onClick={onClose} />
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} className="relative w-[90vw] max-w-4xl md:w-[800px] lg:w-[900px]">
      {renderHeader()}

      {tableData.length ? (
        <DataTable data={tableData} columns={paymentAttemptsColumns} className="mt-5 w-full overflow-x-auto [&_table]:table-auto" />
      ) : (
        <EmptyPaymentAttempts />
      )}
    </Modal>
  );
};
